<template>
  <div>
    <ReportItemContainer v-if="isReportMode">
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div
          v-if="showAnswer"
          v-for="(person, index) in persons"
          class="mb-3"
          :key="index"
        >
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("position") }}: </span
            >{{ person.position }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("name") }}: </span
            >{{ person.name }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("surname") }}: </span
            >{{ person.surname }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("dateOfBirth") }}: </span
            >{{ person.birth }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("nationality") }}: </span
            >{{ person.nationality }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("share") }}: </span
            >{{ person.share }}%
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("address") }}: </span
            >{{ person.address.street }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("city") }}: </span
            >{{ person.address.city }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("country") }}: </span
            >{{ person.address.country }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("pep") }}: </span
            >{{ getBooleanDisplayText(person.pep) }}
          </p>
          <p v-if="person.pep === StringBoolean.Yes">
            <span class="report-item-container__label"
              >{{ getLabel("role") }}: </span
            >{{ person.pepPosition }}
          </p>
          <p v-if="person.pep === StringBoolean.Yes">
            <span class="report-item-container__label"
              >{{ getLabel("country") }}: </span
            >{{ person.pepCountry }}
          </p>
        </div>
      </template>
    </ReportItemContainer>

    <div v-else>
      <v-row>
        <v-col cols="12" sm="6" md="12">
          <p class="mb-5">
            {{ question.text }}
            <InfoPopup
              v-if="question.helpText"
              :title="question.helpTitle"
              :content="question.helpText"
            />
          </p>
        </v-col>
      </v-row>
      <div v-for="(person, index) in persons" class="mb-8" :key="index">
        <v-divider v-if="index !== 0" class="mb-5"></v-divider>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('name')"
              :value="person.name"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.name = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('surname')"
              :value="person.surname"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.surname = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("position") }}</p>
            <PCombobox
              :placeholder="getLabel('positionPlaceholder')"
              :items="managementPositions"
              :select-values="[person.position]"
              :disabled="isReadOnlyMode"
              @field-updated="(val: string[]) => { (person.position = val.length === 0 ? '' : val[0]); emitNewPersons() }"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('yearOfBirth')"
              :placeholder="getLabel('yearOfBirthPlaceholder')"
              :value="person.birth"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.birth = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("nationality") }}</p>
            <PAutocomplete
              :model-value="person.nationality"
              :items="nationalities"
              :select-text="person.nationality"
              :disabled="isReadOnlyMode"
              @field-updated="(val: string) => { (person.nationality = val); emitNewPersons() }"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('share')"
              :value="person.share"
              :suffix="'%'"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.share = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('address')"
              :value="person.address.street"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.address.street = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('city')"
              :value="person.address.city"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.address.city = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("country") }}</p>
            <PAutocomplete
              :model-value="person.address.country"
              :items="itemsStore.countryNames"
              :select-text="person.address.country"
              :placeholder="'Select country'"
              :disabled="isReadOnlyMode"
              @field-updated="(val: string) => { (person.address.country = val); emitNewPersons() }"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="6" md="6">
            <p class="mb-4">{{ getLabel("isPEP") }}</p>
            <PCheckBox
              :label="getBooleanDisplayText('yes')"
              :value="person.pep === StringBoolean.Yes"
              :disabled="isReadOnlyMode"
              @value-changed="setPepValue(person, 'yes')"
            />
            <PCheckBox
              class="mt-3"
              :label="getBooleanDisplayText('no')"
              :value="person.pep === StringBoolean.No"
              :disabled="isReadOnlyMode"
              @value-changed="setPepValue(person, 'no')"
            />
          </v-col>
        </v-row>
        <v-row v-if="person.pep === StringBoolean.Yes" class="mt-5">
          <v-col cols="12" sm="6" md="6">
            <div class="d-flex align-center">
              <p>{{ getLabel("role") }}</p>
              <InfoPopup
                :title="getLabel('role')"
                :content="getLabel('roleHelpText')"
              />
            </div>
            <PInputField
              :value="person.pepPosition"
              @updateEvent="
                (val) => {
                  person.pepPosition = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <p>{{ getLabel("country") }}</p>
            <PAutocomplete
              :model-value="person.pepCountry"
              :items="itemsStore.countryNames"
              :select-text="person.pepCountry"
              @field-updated="(val: string) => { (person.pepCountry = val); emitNewPersons() }"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pl-0">
            <v-btn
              size="medium"
              variant="plain"
              :disabled="isReadOnlyMode"
              @click="addPerson()"
              class="ma-2 mb-15"
              v-if="index == persons.length - 1"
            >
              <v-icon icon="mdi-plus"></v-icon
              ><u class="mb-1">{{ getLabel("addPerson") }}</u>
            </v-btn>
          </v-col>
          <v-col class="text-right">
            <v-btn
              size="medium"
              variant="plain"
              @click="removePerson(person)"
              class="ma-2 mb-15"
              v-if="persons.length > 1"
            >
              <v-icon icon="mdi-delete"></v-icon
              ><u class="mb-1">Remove person</u>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import { nationalities } from "@/modules/nationalities";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";
import {
  BeneficialOwner,
  AnswerBeneficialOwner,
} from "@/models/persons/beneficialOwner";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import PInputField from "@/components/custom/PInputField.vue";
import { useItemsStore } from "@/stores/itemsStore";
import PCheckBox from "@/components/custom/PCheckBox.vue";
import { QuestionnaireTemplateCommonItemsId } from "@/models/enum/questionnaireTemplateCommonItemsId";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { StringBoolean } from "@/models/enum/stringBoolean";
import PCombobox from "@/components/custom/PCombobox.vue";
import { ownershipAnswerComplete } from "@/components/questionnaire/questionnaireUtils";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: AnswerBeneficialOwner[] | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: AnswerBeneficialOwner[]): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const booleanAnswerDisplayText = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.Boolean
);

const beneficialOwnerDisplayTextItems =
  selectedQuestionnaireStore.getBeneficialOwnerDisplayTextItems();

const managementPositions = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.ManagementPositions
);

const itemsStore = useItemsStore();
const persons: Ref<BeneficialOwner[]> = ref(getBeneficialOwners());
const question = computed(() => props.questionModel?.question);

function emitNewPersons(): void {
  const answerPersons = persons.value.map((x) => x.toAnswerModel());
  emit("qEvent", answerPersons);
}

function emitValid(isValid: boolean, fieldId: string): void {
  emit("isValid", isValid, `${question.value.id}__${fieldId}`);
}

function addPerson(): void {
  persons.value.push(new BeneficialOwner());
}

function removePerson(item: BeneficialOwner): void {
  const index = persons.value.indexOf(item);
  persons.value.splice(index, 1);
  emitNewPersons();
}

function setPepValue(person: BeneficialOwner, pepValue: string): void {
  person.setPep(pepValue);
  emitNewPersons();
}

function getBeneficialOwners(): BeneficialOwner[] {
  if (
    !props.answer ||
    props.answer.length == 0 ||
    managementPositions.length == 0
  ) {
    return [new BeneficialOwner()];
  }

  return props.answer.map((answerBeneficialOwner: AnswerBeneficialOwner) => {
    return new BeneficialOwner(answerBeneficialOwner);
  });
}

function getBooleanDisplayText(answerId: string): string {
  const entry = booleanAnswerDisplayText.find((x) => x.id == answerId);
  return entry?.text ?? "";
}

function getLabel(id: string): string {
  return beneficialOwnerDisplayTextItems.find((x) => x.id == id)?.text ?? "";
}

const showAnswer = computed(() => ownershipAnswerComplete(props.answer));
</script>
